/**
 * iter87.168 — Site Admin → Admin Ops → LLM Monitor
 *
 * Read-only surface for site admins to inspect what the LLMs have
 * actually been writing. Collapsible sections:
 *   - Ride Story        (per-tournament season recap prose)
 *   - Rivalries Intel   (per-week Sharp/Trap picks JSON — NFL/EPL/La Liga/UCL group stage)
 *   - Series Intel      (MLB/NBA/MLS/NWSL Series Predictor Sharp/Trap JSON)
 *   - Playoff Intel     (NFL/CFB Playoff Predictor Sharp/Trap JSON)
 *   - Champions Intel   (UCL Final — single match — Advance Predictor)
 *   - Two-Legged Intel  (UCL 2-leg knockout ties — Advance Predictor)
 *
 * The last four all read from the SAME backend endpoint/collection
 * (`playoff_series_intel_cache`, one doc per sport+round_key[+scope])
 * — sliced client-side by (sport, scope) to match how
 * `playoff_series_intel_llm.py::_system_for` actually groups them for
 * the LLM (Series Predictor vs Playoff Predictor vs Advance
 * Predictor). See CHANGELOG.md iter87.495.
 *
 * Each section renders the latest N rows newest-first, with a
 * click-through modal to see full copy. Metadata surfaced: league,
 * tournament, template, generated_at, model, and per-category
 * counters (word count / candidate count / sharp+trap counts).
 *
 * Endpoints:
 *   GET /api/site-admin/llm-monitor/ride-story?limit=20
 *   GET /api/site-admin/llm-monitor/ride-story/{tid}     (full text)
 *   GET /api/site-admin/llm-monitor/rivalries-intel?limit=20  (payload inlined)
 *   GET /api/site-admin/llm-monitor/series-intel?limit=20     (payload inlined, feeds all 4 playoff-family sections)
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Copy, Sparkles, Zap, Trophy, Dices, X, RefreshCw, PauseCircle, PlayCircle, AlertTriangle, RefreshCcw, Loader2 } from "lucide-react";
import { useBrutalConfirm } from "@/components/BrutalConfirm";

const TEMPLATE_LABEL = {
    nfl_boost_api: { label: "NFL Boost", tone: "text-sky-400 border-sky-500/40 bg-sky-500/10" },
    nfl_season_api: { label: "NFL", tone: "text-sky-400 border-sky-500/40 bg-sky-500/10" },
    premier_league_api: { label: "EPL", tone: "text-purple-400 border-purple-500/40 bg-purple-500/10" },
    la_liga_api: { label: "La Liga", tone: "text-orange-400 border-orange-500/40 bg-orange-500/10" },
    champions_league_api: { label: "Champions League", tone: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
    ucl_api: { label: "UCL", tone: "text-indigo-400 border-indigo-500/40 bg-indigo-500/10" },
    world_cup_api: { label: "World Cup", tone: "text-red-400 border-red-500/40 bg-red-500/10" },
    golf_pickem_lowest_avg_api: { label: "Golf LAS", tone: "text-lime-400 border-lime-500/40 bg-lime-500/10" },
    golf_position_draft_api: { label: "Golf Pos", tone: "text-lime-400 border-lime-500/40 bg-lime-500/10" },
};

function TemplatePill({ template }) {
    if (!template) return null;
    const info = TEMPLATE_LABEL[template] || { label: template, tone: "text-zinc-400 border-zinc-600 bg-zinc-800/40" };
    return (
        <span
            data-testid={`llm-monitor-template-pill-${template}`}
            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wider border ${info.tone}`}
        >
            {info.label}
        </span>
    );
}

function fmtRelative(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString();
    } catch { return iso; }
}

function CopyButton({ text, testId }) {
    const [done, setDone] = useState(false);
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(text);
                    setDone(true);
                    setTimeout(() => setDone(false), 1500);
                    toast.success("Copied to clipboard");
                } catch {
                    toast.error("Copy failed");
                }
            }}
            className="brutal-btn-ghost text-[10px] py-1 px-2 flex items-center gap-1"
        >
            <Copy className="w-3 h-3" />
            {done ? "Copied" : "Copy"}
        </button>
    );
}

function RideStoryCard({ row, onOpen, onRegen, regenerating }) {
    return (
        <div
            data-testid={`llm-monitor-ride-story-row-${row.tournament_id}`}
            className="brutal-card-light p-3 flex items-start justify-between gap-3 hover:border-cyber/40 transition-colors"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-sm text-white truncate">{row.tournament_name || "(unnamed)"}</span>
                    <TemplatePill template={row.template} />
                </div>
                <div className="text-[10px] text-zinc-500 mb-1.5 flex items-center gap-2 flex-wrap">
                    <span>{row.league_name || row.league_id?.slice(0, 8)}</span>
                    <span>·</span>
                    <span>{fmtRelative(row.generated_at)}</span>
                    <span>·</span>
                    <span>{row.word_count} words</span>
                </div>
                <p className="text-[11px] text-zinc-400 line-clamp-2 leading-snug">{row.preview}…</p>
            </div>
            {/* iter87.185 — Actions stack (See copy + Regenerate). REGEN wipes
                the cached story on the tournament doc and force-generates
                a fresh one. Super-admin only via backend gate. */}
            <div className="flex flex-col items-stretch gap-1.5 shrink-0">
                <button
                    type="button"
                    data-testid={`llm-monitor-see-ride-story-${row.tournament_id}`}
                    onClick={onOpen}
                    className="brutal-btn-ghost text-[10px] py-1 px-2 whitespace-nowrap"
                >
                    See copy
                </button>
                <button
                    type="button"
                    data-testid={`llm-monitor-regen-ride-story-${row.tournament_id}`}
                    onClick={onRegen}
                    disabled={regenerating}
                    className="brutal-btn-ghost text-[10px] py-1 px-2 whitespace-nowrap flex items-center justify-center gap-1 disabled:opacity-40"
                >
                    {regenerating ? (
                        <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Regen…
                        </>
                    ) : (
                        <>
                            <RefreshCcw className="w-3 h-3" />
                            Regen
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

// iter87.608.2 — Squares Recap roast card. Read-only ("See copy" only
// — no regenerate button: exactly one story per tournament, ever, by
// design, so there's nothing for an admin to re-trigger here).
function SquaresCheerCard({ row, onOpen }) {
    return (
        <div
            data-testid={`llm-monitor-squares-cheer-row-${row.tournament_id}`}
            className="brutal-card-light p-3 flex items-start justify-between gap-3 hover:border-cyber/40 transition-colors"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-sm text-white truncate">{row.tournament_name || "(unnamed)"}</span>
                </div>
                <div className="text-[10px] text-zinc-500 mb-1.5 flex items-center gap-2 flex-wrap">
                    <span>{row.league_name || row.league_id?.slice(0, 8)}</span>
                    <span>·</span>
                    <span>{fmtRelative(row.generated_at)}</span>
                    <span>·</span>
                    <span>{row.word_count} words</span>
                </div>
                <p className="text-[11px] text-zinc-400 line-clamp-2 leading-snug">{row.preview}…</p>
            </div>
            <button
                type="button"
                data-testid={`llm-monitor-see-squares-cheer-${row.tournament_id}`}
                onClick={onOpen}
                className="brutal-btn-ghost text-[10px] py-1 px-2 shrink-0 whitespace-nowrap"
            >
                See copy
            </button>
        </div>
    );
}

function IntelCard({ row, onOpen }) {
    return (
        <div
            data-testid={`llm-monitor-intel-row-${row.tournament_id}-${row.week_key}`}
            className="brutal-card-light p-3 flex items-start justify-between gap-3 hover:border-cyber/40 transition-colors"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-sm text-white truncate">{row.tournament_name || "(unnamed)"}</span>
                    <TemplatePill template={row.template} />
                    <span className="text-[9px] text-zinc-500 font-mono uppercase">wk {row.week_key}</span>
                </div>
                <div className="text-[10px] text-zinc-500 mb-1.5 flex items-center gap-2 flex-wrap">
                    <span>{row.league_name || "—"}</span>
                    <span>·</span>
                    <span>{fmtRelative(row.generated_at)}</span>
                    <span>·</span>
                    {/* iter87.199.21 — Sharp Pick replaces the retired
                        Best Picks column. `best_count` is always 0
                        post-iter87.199.17. */}
                    <span className="text-cyber">{row.sharp_count} sharp</span>
                    <span>·</span>
                    <span className="text-amber-400">{row.trap_count} trap</span>
                    <span>·</span>
                    <span>{row.candidate_count} candidates</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                    {(row.sharps || []).slice(0, 2).map((s, i) => (
                        <span key={`s${i}`} className="text-[10px] text-cyber truncate max-w-[220px]" title={s.why} data-testid={`llm-monitor-row-sharp-${i}`}>
                            ★ {s.team}
                        </span>
                    ))}
                    {(row.trap || []).slice(0, 2).map((tr, i) => (
                        <span key={`t${i}`} className="text-[10px] text-amber-300 truncate max-w-[220px]" title={tr.why}>
                            ⚠ {tr.team}
                        </span>
                    ))}
                </div>
            </div>
            <button
                type="button"
                data-testid={`llm-monitor-see-intel-${row.tournament_id}-${row.week_key}`}
                onClick={onOpen}
                className="brutal-btn-ghost text-[10px] py-1 px-2 shrink-0 whitespace-nowrap"
            >
                See copy
            </button>
        </div>
    );
}

function SeriesIntelCard({ row, onOpen, sectionId = "series-intel" }) {
    const SPORT_LABELS = { mlb: "MLB", nba: "NBA", mls: "MLS", nfl: "NFL", cfb: "CFB", nwsl: "NWSL", ucl: "UCL" };
    const sportLabel = SPORT_LABELS[row.sport] || (row.sport || "").toUpperCase();
    return (
        <div
            data-testid={`llm-monitor-${sectionId}-row-${row.sport}-${row.round_key}`}
            className="brutal-card-light p-3 flex items-start justify-between gap-3 hover:border-cyber/40 transition-colors"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-sm text-white truncate">{row.round_label || row.round_key || "(round)"}</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wider border text-cyber border-cyber/40 bg-cyber/10">
                        {sportLabel}
                    </span>
                    {row.projected && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wider border text-amber-400 border-amber-500/40 bg-amber-500/10">
                            Projected
                        </span>
                    )}
                </div>
                <div className="text-[10px] text-zinc-500 mb-1.5 flex items-center gap-2 flex-wrap">
                    <span>{row.series_count} series</span>
                    <span>·</span>
                    <span>{fmtRelative(row.generated_at)}</span>
                    <span>·</span>
                    <span className="text-cyber">{row.sharp_count} sharp</span>
                    <span>·</span>
                    <span className="text-amber-400">{row.trap_count} trap</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                    {(row.sharps || []).slice(0, 1).map((s, i) => (
                        <span key={`s${i}`} className="text-[10px] text-cyber truncate max-w-[220px]" title={s.why} data-testid={`llm-monitor-${sectionId}-row-sharp-${i}`}>
                            ★ {s.team}
                        </span>
                    ))}
                    {(row.trap_picks || []).slice(0, 1).map((tr, i) => (
                        <span key={`t${i}`} className="text-[10px] text-amber-300 truncate max-w-[220px]" title={tr.why}>
                            ⚠ {tr.team}
                        </span>
                    ))}
                </div>
            </div>
            <button
                type="button"
                data-testid={`llm-monitor-see-${sectionId}-${row.sport}-${row.round_key}`}
                onClick={onOpen}
                className="brutal-btn-ghost text-[10px] py-1 px-2 shrink-0 whitespace-nowrap"
            >
                See copy
            </button>
        </div>
    );
}

function Modal({ open, onClose, title, children, testId }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid={testId}
        >
            <div
                className="brutal-card max-w-2xl w-full max-h-[85dvh] overflow-y-auto p-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-3">
                    <h3 className="text-sm font-bold uppercase tracking-widest text-white pr-4">{title}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-zinc-500 hover:text-white shrink-0"
                        data-testid="llm-monitor-modal-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

export default function LLMMonitorTab() {
    const [rideRows, setRideRows] = useState([]);
    const [intelRows, setIntelRows] = useState([]);
    const [seriesIntelRows, setSeriesIntelRows] = useState([]);
    const [squaresRows, setSquaresRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [rideOpen, setRideOpen] = useState(true);
    const [intelOpen, setIntelOpen] = useState(true);
    const [seriesIntelOpen, setSeriesIntelOpen] = useState(true);
    const [squaresOpen, setSquaresOpen] = useState(true);
    // iter87.495 (user-asked — "add the corresponding LLM monitor
    // sections — playoff intel, champions intel, two-legged intel")
    // — three more sections sliced from the same `seriesIntelRows`
    // payload by (sport, scope), matching how `playoff_series_intel_
    // llm.py::_system_for` actually groups these sports for the LLM
    // (Series Predictor vs Playoff Predictor vs Advance Predictor).
    const [playoffOpen, setPlayoffOpen] = useState(true);
    const [championsOpen, setChampionsOpen] = useState(true);
    const [twoLegOpen, setTwoLegOpen] = useState(true);
    const [modalRide, setModalRide] = useState(null);   // { row, text?, loading? }
    const [modalIntel, setModalIntel] = useState(null); // row
    const [modalSeriesIntel, setModalSeriesIntel] = useState(null); // row
    const [modalSquares, setModalSquares] = useState(null); // row
    // iter87.170 — Universal Pause circuit breaker state.
    const [pauseState, setPauseState] = useState(null); // { paused, paused_at, paused_by, reason }
    const [pauseModal, setPauseModal] = useState(null); // { intent: "pause"|"resume", reason: string }

    // iter87.495 — Series Predictor (MLB/NBA/MLS/NWSL) vs Playoff
    // Predictor (NFL/CFB) vs UCL's own Final/Two-Leg Advance Predictor
    // tiles. UCL rows without a recognized `scope` (shouldn't happen —
    // every UCL doc is written with scope="final" or "two_leg" — see
    // `playoff_series_intel.py`) fall into neither new bucket and stay
    // out of all four lists rather than silently miscategorized.
    const coreSeriesRows = seriesIntelRows.filter(r => !["nfl", "cfb", "ucl"].includes(r.sport));
    const playoffRows = seriesIntelRows.filter(r => ["nfl", "cfb"].includes(r.sport));
    const championsRows = seriesIntelRows.filter(r => r.sport === "ucl" && r.scope === "final");
    const twoLegRows = seriesIntelRows.filter(r => r.sport === "ucl" && r.scope === "two_leg");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [rs, ri, si, ps, sq] = await Promise.all([
                    api.siteLLMMonitorRideStory({ limit: 20 }),
                    api.siteLLMMonitorRivalriesIntel({ limit: 20 }),
                    api.siteLLMMonitorSeriesIntel({ limit: 20 }),
                    api.siteLLMMonitorPauseStatus(),
                    api.siteLLMMonitorSquaresCheer({ limit: 20 }),
                ]);
                if (cancelled) return;
                setRideRows(rs?.items || []);
                setIntelRows(ri?.items || []);
                setSeriesIntelRows(si?.items || []);
                setPauseState(ps || null);
                setSquaresRows(sq?.items || []);
            } catch (e) {
                if (!cancelled) toast.error("Failed to load LLM monitor: " + (e?.message || e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [refreshKey]);

    // iter87.170 — Auto-refresh pause status every 30s so multiple admins
    // see current state without hammering refresh.
    useEffect(() => {
        const id = setInterval(async () => {
            try {
                const ps = await api.siteLLMMonitorPauseStatus();
                setPauseState(ps);
            } catch { /* ignore */ }
        }, 30000);
        return () => clearInterval(id);
    }, []);

    const confirmPauseToggle = async () => {
        if (!pauseModal) return;
        const nextPaused = pauseModal.intent === "pause";
        try {
            const ps = await api.siteLLMMonitorSetPause({
                paused: nextPaused,
                reason: nextPaused ? (pauseModal.reason || null) : null,
            });
            setPauseState(ps);
            setPauseModal(null);
            toast.success(nextPaused ? "AI features paused." : "AI features resumed.");
        } catch (e) {
            toast.error("Failed to update pause state: " + (e?.message || e));
        }
    };

    const openRideModal = async (row) => {
        setModalRide({ row, loading: true });
        try {
            const detail = await api.siteLLMMonitorRideStoryDetail(row.tournament_id);
            setModalRide({ row: detail, loading: false });
        } catch (e) {
            toast.error("Failed to load ride story: " + (e?.message || e));
            setModalRide(null);
        }
    };

    // iter87.185 — Regenerate one Ride Story. Confirm → POST → toast → hot-swap row.
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();
    const [regenTid, setRegenTid] = useState(null);
    const regenerateRideStory = async (row) => {
        const proceed = await brutalConfirm({
            title: "Regenerate this Ride Story?",
            body: `Wipes the cached story on "${row.tournament_name || row.tournament_id}" (${row.league_name || row.league_id?.slice(0, 8)}) and generates a fresh one using the current recap snapshot data. Costs ~1–3¢ against the shared LLM budget. Old copy is not recoverable.`,
            confirmLabel: "Regenerate",
            cancelLabel: "Cancel",
            destructive: true,
        });
        if (!proceed) return;
        setRegenTid(row.tournament_id);
        try {
            const fresh = await api.siteLLMMonitorRegenerateRideStory(row.tournament_id);
            toast.success(`Ride Story regenerated · ${fresh.word_count} words`);
            // Hot-swap the row in place — text preview + generated_at update.
            setRideRows((prev) => prev.map((r) => r.tournament_id === row.tournament_id ? {
                ...r,
                generated_at: fresh.generated_at,
                model: fresh.model,
                word_count: fresh.word_count,
                preview: String(fresh.text || "").slice(0, 220),
            } : r));
        } catch (e) {
            toast.error(e?.response?.data?.detail || `Regenerate failed: ${e?.message || e}`);
        } finally {
            setRegenTid(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="llm-monitor-tab">
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-bold uppercase tracking-wider text-white flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-cyber" /> LLM Monitor
                        </h2>
                        <p className="text-[11px] text-zinc-500 mt-0.5">
                            The 20 most recent Ride Story recaps, Squares Recap roasts, Rivalries Intel weekly picks, Series/Playoff/Champions/Two-Legged Intel picks. Click any row to see full copy + metadata.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setRefreshKey((k) => k + 1)}
                        className="brutal-btn-ghost text-xs py-2 px-3 flex items-center gap-1.5"
                        data-testid="llm-monitor-refresh-btn"
                    >
                        <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* iter87.170 — Universal Pause circuit breaker card. */}
            {pauseState && (
                <section
                    data-testid="llm-monitor-pause-card"
                    className={`brutal-card p-4 flex items-start justify-between gap-4 ${pauseState.paused ? "border-heat/60 bg-heat/5" : "border-emerald-500/40 bg-emerald-500/5"}`}
                >
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            {pauseState.paused
                                ? <PauseCircle className="w-4 h-4 text-heat shrink-0" />
                                : <PlayCircle className="w-4 h-4 text-emerald-400 shrink-0" />}
                            <span
                                className={`text-sm font-bold uppercase tracking-wider ${pauseState.paused ? "text-heat" : "text-emerald-400"}`}
                                data-testid="llm-monitor-pause-status-label"
                            >
                                {pauseState.paused ? "AI Paused" : "AI Active"}
                            </span>
                        </div>
                        {pauseState.paused ? (
                            <div className="text-[11px] text-zinc-400 space-y-0.5">
                                <div>Since {fmtRelative(pauseState.paused_at)} by <span className="text-white">{pauseState.paused_by || "site-admin"}</span></div>
                                {pauseState.reason && (
                                    <div className="text-zinc-500 italic">Reason: {pauseState.reason}</div>
                                )}
                                <div className="text-[10px] text-zinc-600 mt-1 flex items-start gap-1">
                                    <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                                    <span>Ride Story + Squares Recap + Rivalries Intel + Series/Playoff/Champions/Two-Legged Intel generation is blocked. Cached content still visible; only new generations short-circuit.</span>
                                </div>
                            </div>
                        ) : (
                            <div className="text-[11px] text-zinc-500">
                                Ride Story, Squares Recap, Rivalries Intel, and Series/Playoff/Champions/Two-Legged Intel are all running normally. Flip pause if you spot a runaway loop or bad output landing on production.
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        data-testid="llm-monitor-pause-toggle-btn"
                        onClick={() => setPauseModal({
                            intent: pauseState.paused ? "resume" : "pause",
                            reason: "",
                        })}
                        className={`brutal-btn text-xs py-2 px-3 shrink-0 whitespace-nowrap ${pauseState.paused ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/30" : "bg-heat/20 border-heat/60 text-heat hover:bg-heat/30"}`}
                    >
                        {pauseState.paused ? "Resume AI" : "Pause AI"}
                    </button>
                </section>
            )}

            {/* Ride Story section */}
            <section data-testid="llm-monitor-ride-story-section">
                <button
                    type="button"
                    onClick={() => setRideOpen((o) => !o)}
                    className="w-full flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-cyber transition-colors mb-2"
                    data-testid="llm-monitor-ride-story-toggle"
                >
                    {rideOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <Sparkles className="w-3 h-3" />
                    Ride Story · {rideRows.length}
                </button>
                {rideOpen && (
                    <div className="space-y-1.5">
                        {loading && <p className="text-xs text-zinc-500 italic px-2 py-3">Loading…</p>}
                        {!loading && rideRows.length === 0 && (
                            <p className="text-xs text-zinc-500 italic px-2 py-3">No ride stories generated yet.</p>
                        )}
                        {rideRows.map((r) => (
                            <RideStoryCard
                                key={r.tournament_id}
                                row={r}
                                onOpen={() => openRideModal(r)}
                                onRegen={() => regenerateRideStory(r)}
                                regenerating={regenTid === r.tournament_id}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* iter87.608.2 — Squares Recap section (read-only — see squares_cheer.py module docstring for the single trigger condition + why there's no regenerate button here). */}
            <section data-testid="llm-monitor-squares-cheer-section">
                <button
                    type="button"
                    onClick={() => setSquaresOpen((o) => !o)}
                    className="w-full flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-cyber transition-colors mb-2"
                    data-testid="llm-monitor-squares-cheer-toggle"
                >
                    {squaresOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <Dices className="w-3 h-3" />
                    Squares Recap · {squaresRows.length}
                </button>
                {squaresOpen && (
                    <div className="space-y-1.5">
                        {loading && <p className="text-xs text-zinc-500 italic px-2 py-3">Loading…</p>}
                        {!loading && squaresRows.length === 0 && (
                            <p className="text-xs text-zinc-500 italic px-2 py-3">No Squares recaps generated yet.</p>
                        )}
                        {squaresRows.map((r) => (
                            <SquaresCheerCard key={r.tournament_id} row={r} onOpen={() => setModalSquares(r)} />
                        ))}
                    </div>
                )}
            </section>

            {/* Rivalries Intel section */}
            <section data-testid="llm-monitor-intel-section">
                <button
                    type="button"
                    onClick={() => setIntelOpen((o) => !o)}
                    className="w-full flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-cyber transition-colors mb-2"
                    data-testid="llm-monitor-intel-toggle"
                >
                    {intelOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <Zap className="w-3 h-3" />
                    Rivalries Intel · {intelRows.length}
                </button>
                {intelOpen && (
                    <div className="space-y-1.5">
                        {loading && <p className="text-xs text-zinc-500 italic px-2 py-3">Loading…</p>}
                        {!loading && intelRows.length === 0 && (
                            <p className="text-xs text-zinc-500 italic px-2 py-3">No rivalries intel generated yet.</p>
                        )}
                        {intelRows.map((r) => (
                            <IntelCard key={`${r.tournament_id}-${r.week_key}`} row={r} onOpen={() => setModalIntel(r)} />
                        ))}
                    </div>
                )}
            </section>

            {/* Series Intel section */}
            <section data-testid="llm-monitor-series-intel-section">
                <button
                    type="button"
                    onClick={() => setSeriesIntelOpen((o) => !o)}
                    className="w-full flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-cyber transition-colors mb-2"
                    data-testid="llm-monitor-series-intel-toggle"
                >
                    {seriesIntelOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <Trophy className="w-3 h-3" />
                    Series Intel · {coreSeriesRows.length}
                </button>
                {seriesIntelOpen && (
                    <div className="space-y-1.5">
                        {loading && <p className="text-xs text-zinc-500 italic px-2 py-3">Loading…</p>}
                        {!loading && coreSeriesRows.length === 0 && (
                            <p className="text-xs text-zinc-500 italic px-2 py-3">No series intel generated yet.</p>
                        )}
                        {coreSeriesRows.map((r) => (
                            <SeriesIntelCard key={`${r.sport}-${r.round_key}`} row={r} onOpen={() => setModalSeriesIntel(r)} />
                        ))}
                    </div>
                )}
            </section>

            {/* iter87.495 (user-asked) — Playoff Intel section (NFL/CFB Playoff Predictor). */}
            <section data-testid="llm-monitor-playoff-intel-section">
                <button
                    type="button"
                    onClick={() => setPlayoffOpen((o) => !o)}
                    className="w-full flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-cyber transition-colors mb-2"
                    data-testid="llm-monitor-playoff-intel-toggle"
                >
                    {playoffOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <Trophy className="w-3 h-3" />
                    Playoff Intel · {playoffRows.length}
                </button>
                {playoffOpen && (
                    <div className="space-y-1.5">
                        {loading && <p className="text-xs text-zinc-500 italic px-2 py-3">Loading…</p>}
                        {!loading && playoffRows.length === 0 && (
                            <p className="text-xs text-zinc-500 italic px-2 py-3">No playoff intel generated yet.</p>
                        )}
                        {playoffRows.map((r) => (
                            <SeriesIntelCard key={`${r.sport}-${r.round_key}`} row={r} sectionId="playoff-intel" onOpen={() => setModalSeriesIntel(r)} />
                        ))}
                    </div>
                )}
            </section>

            {/* iter87.495 (user-asked) — Champions Intel section (UCL Final, single match — Advance Predictor). */}
            <section data-testid="llm-monitor-champions-intel-section">
                <button
                    type="button"
                    onClick={() => setChampionsOpen((o) => !o)}
                    className="w-full flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-cyber transition-colors mb-2"
                    data-testid="llm-monitor-champions-intel-toggle"
                >
                    {championsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <Trophy className="w-3 h-3" />
                    Champions Intel · {championsRows.length}
                </button>
                {championsOpen && (
                    <div className="space-y-1.5">
                        {loading && <p className="text-xs text-zinc-500 italic px-2 py-3">Loading…</p>}
                        {!loading && championsRows.length === 0 && (
                            <p className="text-xs text-zinc-500 italic px-2 py-3">No Champions (UCL Final) intel generated yet.</p>
                        )}
                        {championsRows.map((r) => (
                            <SeriesIntelCard key={`${r.sport}-${r.round_key}-final`} row={r} sectionId="champions-intel" onOpen={() => setModalSeriesIntel(r)} />
                        ))}
                    </div>
                )}
            </section>

            {/* iter87.495 (user-asked) — Two-Legged Intel section (UCL 2-leg knockout ties — Advance Predictor). */}
            <section data-testid="llm-monitor-two-leg-intel-section">
                <button
                    type="button"
                    onClick={() => setTwoLegOpen((o) => !o)}
                    className="w-full flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-cyber transition-colors mb-2"
                    data-testid="llm-monitor-two-leg-intel-toggle"
                >
                    {twoLegOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <Trophy className="w-3 h-3" />
                    Two-Legged Intel · {twoLegRows.length}
                </button>
                {twoLegOpen && (
                    <div className="space-y-1.5">
                        {loading && <p className="text-xs text-zinc-500 italic px-2 py-3">Loading…</p>}
                        {!loading && twoLegRows.length === 0 && (
                            <p className="text-xs text-zinc-500 italic px-2 py-3">No Two-Legged (UCL) intel generated yet.</p>
                        )}
                        {twoLegRows.map((r) => (
                            <SeriesIntelCard key={`${r.sport}-${r.round_key}-two_leg`} row={r} sectionId="two-leg-intel" onOpen={() => setModalSeriesIntel(r)} />
                        ))}
                    </div>
                )}
            </section>

            {/* Ride story detail modal */}
            <Modal
                open={!!modalRide}
                onClose={() => setModalRide(null)}
                title="Ride Story · Full copy"
                testId="llm-monitor-ride-story-modal"
            >
                {modalRide?.loading && <p className="text-xs text-zinc-500 italic">Loading…</p>}
                {modalRide && !modalRide.loading && (
                    <>
                        <div className="mb-3 flex items-center gap-2 flex-wrap text-[10px] text-zinc-500">
                            <span className="font-bold text-white text-sm">{modalRide.row.tournament_name}</span>
                            <TemplatePill template={modalRide.row.template} />
                        </div>
                        <div className="text-[10px] text-zinc-500 mb-3 flex gap-3 flex-wrap">
                            <span>League: {modalRide.row.league_name || modalRide.row.league_id}</span>
                            <span>Model: {modalRide.row.model || "—"}</span>
                            <span>Generated: {fmtRelative(modalRide.row.generated_at)}</span>
                        </div>
                        <pre
                            data-testid="llm-monitor-ride-story-modal-text"
                            className="whitespace-pre-wrap font-sans text-[13px] text-zinc-200 leading-relaxed border-l-2 border-cyber/40 pl-3 mb-3"
                        >
                            {modalRide.row.text}
                        </pre>
                        <div className="flex justify-end">
                            <CopyButton text={modalRide.row.text || ""} testId="llm-monitor-copy-ride-story" />
                        </div>
                    </>
                )}
            </Modal>

            {/* Squares Recap detail modal — text already inlined in the list row, no separate fetch needed */}
            <Modal
                open={!!modalSquares}
                onClose={() => setModalSquares(null)}
                title="Squares Recap · Full copy"
                testId="llm-monitor-squares-cheer-modal"
            >
                {modalSquares && (
                    <>
                        <div className="mb-3 flex items-center gap-2 flex-wrap text-[10px] text-zinc-500">
                            <span className="font-bold text-white text-sm">{modalSquares.tournament_name}</span>
                        </div>
                        <div className="text-[10px] text-zinc-500 mb-3 flex gap-3 flex-wrap">
                            <span>League: {modalSquares.league_name || modalSquares.league_id}</span>
                            <span>Model: {modalSquares.model || "—"}</span>
                            <span>Generated: {fmtRelative(modalSquares.generated_at)}</span>
                        </div>
                        <pre
                            data-testid="llm-monitor-squares-cheer-modal-text"
                            className="whitespace-pre-wrap font-sans text-[13px] text-zinc-200 leading-relaxed border-l-2 border-cyber/40 pl-3 mb-3"
                        >
                            {modalSquares.text}
                        </pre>
                        <div className="flex justify-end">
                            <CopyButton text={modalSquares.text || ""} testId="llm-monitor-copy-squares-cheer" />
                        </div>
                    </>
                )}
            </Modal>

            {/* Rivalries Intel detail modal */}
            <Modal
                open={!!modalIntel}
                onClose={() => setModalIntel(null)}
                title="Rivalries Intel · Full copy"
                testId="llm-monitor-intel-modal"
            >
                {modalIntel && (
                    <>
                        <div className="mb-3 flex items-center gap-2 flex-wrap text-[10px] text-zinc-500">
                            <span className="font-bold text-white text-sm">{modalIntel.tournament_name}</span>
                            <TemplatePill template={modalIntel.template} />
                            <span className="font-mono uppercase">wk {modalIntel.week_key}</span>
                        </div>
                        <div className="text-[10px] text-zinc-500 mb-3 flex gap-3 flex-wrap">
                            <span>League: {modalIntel.league_name || "—"}</span>
                            <span>Generated: {fmtRelative(modalIntel.generated_at)}</span>
                            <span>Candidates: {modalIntel.candidate_count}</span>
                        </div>
                        <div className="space-y-3">
                            <div>
                                {/* iter87.199.21 — Sharp Pick replaces
                                    the retired Best Picks block. */}
                                <h4 className="text-[10px] uppercase tracking-widest text-cyber mb-1.5">Sharp Pick · {modalIntel.sharp_count}</h4>
                                {(modalIntel.sharps || []).map((s, i) => (
                                    <div key={i} className="text-[12px] mb-1.5 pl-3 border-l border-cyber/40" data-testid={`llm-monitor-intel-sharp-${i}`}>
                                        <span className="font-bold text-white">{s.team}</span>
                                        <span className="text-zinc-400"> — {s.why}</span>
                                    </div>
                                ))}
                                {modalIntel.sharp_count === 0 && <p className="text-[11px] text-zinc-600 italic pl-3">(none)</p>}
                            </div>
                            <div>
                                <h4 className="text-[10px] uppercase tracking-widest text-amber-400 mb-1.5">Trap picks · {modalIntel.trap_count}</h4>
                                {(modalIntel.trap || []).map((tr, i) => (
                                    <div key={i} className="text-[12px] mb-1.5 pl-3 border-l border-amber-500/40" data-testid={`llm-monitor-intel-trap-${i}`}>
                                        <span className="font-bold text-white">{tr.team}</span>
                                        <span className="text-zinc-400"> — {tr.why}</span>
                                    </div>
                                ))}
                                {modalIntel.trap_count === 0 && <p className="text-[11px] text-zinc-600 italic pl-3">(none)</p>}
                            </div>
                        </div>
                        <div className="flex justify-end mt-4">
                            <CopyButton
                                text={JSON.stringify({ sharps: modalIntel.sharps, trap: modalIntel.trap }, null, 2)}
                                testId="llm-monitor-copy-intel"
                            />
                        </div>
                    </>
                )}
            </Modal>

            {/* Series/Playoff/Champions/Two-Legged Intel detail modal — shared across all four sections */}
            <Modal
                open={!!modalSeriesIntel}
                onClose={() => setModalSeriesIntel(null)}
                title={
                    modalSeriesIntel?.sport === "ucl" && modalSeriesIntel?.scope === "final" ? "Champions Intel · Full copy"
                    : modalSeriesIntel?.sport === "ucl" && modalSeriesIntel?.scope === "two_leg" ? "Two-Legged Intel · Full copy"
                    : ["nfl", "cfb"].includes(modalSeriesIntel?.sport) ? "Playoff Intel · Full copy"
                    : "Series Intel · Full copy"
                }
                testId="llm-monitor-series-intel-modal"
            >
                {modalSeriesIntel && (
                    <>
                        <div className="mb-3 flex items-center gap-2 flex-wrap text-[10px] text-zinc-500">
                            <span className="font-bold text-white text-sm">{modalSeriesIntel.round_label || modalSeriesIntel.round_key}</span>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wider border text-cyber border-cyber/40 bg-cyber/10">
                                {modalSeriesIntel.sport === "mlb" ? "MLB" : modalSeriesIntel.sport === "nba" ? "NBA" : (modalSeriesIntel.sport || "").toUpperCase()}
                            </span>
                            {modalSeriesIntel.projected && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wider border text-amber-400 border-amber-500/40 bg-amber-500/10">
                                    Projected
                                </span>
                            )}
                        </div>
                        <div className="text-[10px] text-zinc-500 mb-3 flex gap-3 flex-wrap">
                            <span>Generated: {fmtRelative(modalSeriesIntel.generated_at)}</span>
                            <span>Series: {modalSeriesIntel.series_count}</span>
                        </div>
                        {modalSeriesIntel.matchups?.length > 0 && (
                            <div className="text-[11px] text-zinc-400 mb-3 flex flex-wrap gap-x-3 gap-y-0.5">
                                {modalSeriesIntel.matchups.map((m, i) => (
                                    <span key={i} data-testid={`llm-monitor-series-intel-matchup-${i}`}>{m}</span>
                                ))}
                            </div>
                        )}
                        <div className="space-y-3">
                            <div>
                                <h4 className="text-[10px] uppercase tracking-widest text-cyber mb-1.5">Sharp Pick · {modalSeriesIntel.sharp_count}</h4>
                                {(modalSeriesIntel.sharps || []).map((s, i) => (
                                    <div key={i} className="text-[12px] mb-1.5 pl-3 border-l border-cyber/40" data-testid={`llm-monitor-series-intel-sharp-${i}`}>
                                        <span className="font-bold text-white">{s.team}</span>
                                        {s.series_pick && <span className="text-cyber"> ({s.series_pick})</span>}
                                        <span className="text-zinc-400"> — {s.why}</span>
                                    </div>
                                ))}
                                {modalSeriesIntel.sharp_count === 0 && <p className="text-[11px] text-zinc-600 italic pl-3">(none)</p>}
                            </div>
                            <div>
                                <h4 className="text-[10px] uppercase tracking-widest text-amber-400 mb-1.5">Trap picks · {modalSeriesIntel.trap_count}</h4>
                                {(modalSeriesIntel.trap_picks || []).map((tr, i) => (
                                    <div key={i} className="text-[12px] mb-1.5 pl-3 border-l border-amber-500/40" data-testid={`llm-monitor-series-intel-trap-${i}`}>
                                        <span className="font-bold text-white">{tr.team}</span>
                                        {tr.series_pick && <span className="text-amber-400"> ({tr.series_pick})</span>}
                                        <span className="text-zinc-400"> — {tr.why}</span>
                                    </div>
                                ))}
                                {modalSeriesIntel.trap_count === 0 && <p className="text-[11px] text-zinc-600 italic pl-3">(none)</p>}
                            </div>
                        </div>
                        <div className="flex justify-end mt-4">
                            <CopyButton
                                text={JSON.stringify({ sharps: modalSeriesIntel.sharps, trap: modalSeriesIntel.trap_picks }, null, 2)}
                                testId="llm-monitor-copy-series-intel"
                            />
                        </div>
                    </>
                )}
            </Modal>

            {/* iter87.170 — Pause / Resume confirm modal. */}
            <Modal
                open={!!pauseModal}
                onClose={() => setPauseModal(null)}
                title={pauseModal?.intent === "pause" ? "Pause all AI features?" : "Resume all AI features?"}
                testId="llm-monitor-pause-modal"
            >
                {pauseModal && (
                    <>
                        <p className="text-[13px] text-zinc-300 mb-3">
                            {pauseModal.intent === "pause"
                                ? "This stops Ride Story recaps and Rivalries Intel commentary from making any Claude calls. Cached content stays visible; new generations will short-circuit until you resume."
                                : "AI features will resume normally on the next generation cycle. Any queued Ride Story or Rivalries Intel that skipped while paused will regenerate on its next natural trigger."}
                        </p>
                        {pauseModal.intent === "pause" && (
                            <div className="mb-4">
                                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">
                                    Reason (optional)
                                </label>
                                <input
                                    type="text"
                                    data-testid="llm-monitor-pause-reason-input"
                                    value={pauseModal.reason}
                                    onChange={(e) => setPauseModal((m) => ({ ...m, reason: e.target.value }))}
                                    placeholder="e.g. detected loop on CFB intel"
                                    className="w-full bg-[#0a0a0a] border border-[#333] rounded-md px-2 py-1.5 text-[13px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyber"
                                />
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setPauseModal(null)}
                                className="brutal-btn-ghost text-xs py-2 px-3"
                                data-testid="llm-monitor-pause-cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmPauseToggle}
                                data-testid="llm-monitor-pause-confirm-btn"
                                className={`brutal-btn text-xs py-2 px-3 ${pauseModal.intent === "pause" ? "bg-heat/20 border-heat/60 text-heat hover:bg-heat/30" : "bg-emerald-500/20 border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/30"}`}
                            >
                                {pauseModal.intent === "pause" ? "Pause AI" : "Resume AI"}
                            </button>
                        </div>
                    </>
                )}
            </Modal>
            {/* iter87.185 — Confirm host for the Ride Story regenerate button. */}
            {confirmHost}
        </div>
    );
}
